import {
  costUsd,
  type LlmCostAccounting,
  startCostAccountingHeartbeat,
} from '@edgevanta/bid-tab-extract/cost-accounting';
import type { LanguageModel } from 'ai';

interface ModelUsage {
  inputTokens?: number;
  outputTokens?: number;
  cachedInputTokens?: number;
}

interface NormalizedModelUsage {
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
}

function validTokenCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function cacheCreationInputTokens(providerMetadata: unknown): unknown {
  return (
    providerMetadata as {
      anthropic?: { cacheCreationInputTokens?: unknown };
    }
  )?.anthropic?.cacheCreationInputTokens;
}

function normalizeUsage(
  provider: string,
  usage: ModelUsage,
  providerMetadata: unknown,
): NormalizedModelUsage | null {
  const inputTokens = usage.inputTokens;
  const outputTokens = usage.outputTokens;
  const cacheReadTokens = usage.cachedInputTokens ?? 0;
  if (
    !validTokenCount(inputTokens) ||
    !validTokenCount(outputTokens) ||
    !validTokenCount(cacheReadTokens)
  ) {
    return null;
  }

  if (provider.startsWith('openai')) {
    if (cacheReadTokens > inputTokens) {
      return null;
    }
    return {
      inputTokens: inputTokens - cacheReadTokens,
      outputTokens,
      cacheWriteTokens: 0,
      cacheReadTokens,
    };
  }

  if (provider.startsWith('anthropic')) {
    const rawCacheWriteTokens = cacheCreationInputTokens(providerMetadata) ?? 0;
    if (!validTokenCount(rawCacheWriteTokens)) {
      return null;
    }
    return {
      inputTokens,
      outputTokens,
      cacheWriteTokens: rawCacheWriteTokens,
      cacheReadTokens,
    };
  }

  if (cacheReadTokens > 0) {
    return null;
  }
  return {
    inputTokens,
    outputTokens,
    cacheWriteTokens: 0,
    cacheReadTokens: 0,
  };
}

function modelIdentity(model: LanguageModel): {
  provider: string;
  model: string;
} {
  if (typeof model !== 'string') {
    return { provider: model.provider, model: model.modelId };
  }
  const separator = model.indexOf(':');
  return separator > 0
    ? { provider: model.slice(0, separator), model: model.slice(separator + 1) }
    : { provider: 'unknown', model };
}

/**
 * Reserve and finalize one non-Core-2 Custom Bid Tabs model call. Provider
 * retries are disabled by callers so one reservation always represents one
 * paid request. Accounting failures sit outside the provider call and are
 * allowed to abort the workflow, preventing an unmetered retry.
 */
export async function runAccountedCustomBidTabsModelCall<T>(options: {
  stage: string;
  model: LanguageModel;
  costAccounting?: LlmCostAccounting;
  call: () => Promise<{
    value: T;
    usage: ModelUsage;
    providerMetadata?: unknown;
  }>;
}): Promise<T> {
  const { costAccounting, model, stage } = options;
  if (!costAccounting) {
    return (await options.call()).value;
  }

  const identity = modelIdentity(model);
  const reservation = {
    provider: identity.provider,
    model: identity.model,
    stage,
    attempt: 1,
    occurredAt: new Date(),
  };
  const attemptId = await costAccounting.reserveAttempt(reservation);
  const heartbeat = startCostAccountingHeartbeat(costAccounting, attemptId);
  let result: Awaited<ReturnType<typeof options.call>>;
  let heartbeatResult: { failed: boolean; error: unknown };
  try {
    result = await options.call();
  } catch (error) {
    heartbeatResult = await heartbeat.stop();
    await costAccounting.finalizeAttempt({
      ...reservation,
      attemptId,
      inputTokens: null,
      outputTokens: null,
      cacheWriteTokens: null,
      cacheReadTokens: null,
      usd: null,
      status: 'failed',
      ledgerStatus: 'unknown',
    });
    if (heartbeatResult.failed) {
      throw heartbeatResult.error;
    }
    throw error;
  }
  heartbeatResult = await heartbeat.stop();

  const usage = normalizeUsage(
    identity.provider,
    result.usage,
    result.providerMetadata,
  );
  const usd = usage
    ? costUsd({
        model: identity.model,
        uncachedInputTokens: usage.inputTokens,
        outputTokens: usage.outputTokens,
        cacheWriteTokens: usage.cacheWriteTokens,
        cacheReadTokens: usage.cacheReadTokens,
        at: reservation.occurredAt,
      })
    : null;
  await costAccounting.finalizeAttempt({
    ...reservation,
    attemptId,
    inputTokens: usage?.inputTokens ?? null,
    outputTokens: usage?.outputTokens ?? null,
    cacheWriteTokens: usage?.cacheWriteTokens ?? null,
    cacheReadTokens: usage?.cacheReadTokens ?? null,
    usd,
    status: 'ok',
    ledgerStatus: usd === null ? 'unknown' : 'completed',
  });
  if (heartbeatResult.failed) {
    throw heartbeatResult.error;
  }
  if (usd === null) {
    throw new Error(`${stage}: Custom Bid Tabs model cost is unknown`);
  }
  return result.value;
}
